/**
 * SecretsCatalog — **Connections** UI for the reusable tool-secret registry (workspace + network).
 * Lists ``GET /tools/secrets-registry`` rows. **Create New** first asks which MCP tool needs
 * a stored-connection (**service** MCP tools whose catalog schema includes ``x-secret`` fields —
 * ``generic-rest-api``, ``code-connector``, and future equivalents), then opens

 * ``ToolGenericRestConfigWizard`` or ``ToolCodeConnectorRegistryWizard`` accordingly.
 *
 * When ``catalogNetworkId`` is set (network workbench), tenant-wide rows remain visible
 * alongside connections scoped to that network.
 *
 * PR5: listens for ``stepflow-secret-registry-changed`` (user + workbench channel) to
 * silently refresh; “Where used” calls ``GET /tools/secrets-registry/{id}/references``;
 * deletes call ``DELETE /tools/secrets-registry/{id}`` (blocked server-side while agents mount the id).
 *
 * **Probes:** “Test connection” / row **Test** are not gated on having a workspace network.
 * Registry secrets are tenant-scoped; ``POST /tools/probe`` and ``…/test`` authorize via JWT
 * (and ``__registry_probe__`` when no network id is in UI context). Do not add a UI flag to
 * disable probes here — that only hid a valid workflow.
 * The list API includes ``probeMethod`` / ``probePath`` (from saved ``PROBE_*`` or ``API_SPECIFICATION``)
 * because list rows omit ``config`` — row **Test** uses those so the MCP request targets your REST path,
 * not the tenant root (which often 302s to a browser shell such as ``/jira/for-you``).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Cable, Copy, Pencil, Plus, RefreshCw, Search, Trash2, Eye } from 'lucide-react';
import WorkspaceViewDialog from '../workspace/WorkspaceViewDialog.jsx';
import {
  createMcpCredential,
  deleteWorkspace,
  deleteMcpCredential,
  deleteSecretsRegistrySecret,
  getSecretsRegistryReferences,
  getSecretsRegistrySecret,
  listMcpCredentials,
  listMcpRegistry,
  listSecretsRegistry,
  listTools,
  revealMcpCredentialScoped,
  secretsRegistryV1Enabled,
  testMcpCredentialScoped,
  testSecretsRegistrySecret,
  putAgentMcpCredential,
  setAgentMcpState,
  setAgentConnectorState,
  clearAgentConnectorState,
  setAgentNetworkMcpEnablement,
  listConnections,
} from '../../services/toolsApi';
import {
  setAgentPoolConnectorOverride,
} from '../../services/poolMembersApi';
import {
  inheritedConnectionRows,
  connectionDedupKey,
} from '../../utils/connectionsSurface.js';
import { probeRequestFromGenericRestRegistryRow } from '../../services/genericRestProbe';
import ToolGenericRestConfigWizard from '../agentTools/ToolGenericRestConfigWizard.jsx';
import ToolCodeConnectorRegistryWizard, {
  mapRegistryCodeConnectorSecretToInitial,
} from '../agentTools/ToolCodeConnectorRegistryWizard.jsx';
import ToolCredBrokerConfigWizard, {
  mapRegistryCredBrokerSecretToInitial,
} from '../agentTools/ToolCredBrokerConfigWizard.jsx';
import FederatedConnectionWizard, { mapRegistryFederatedToInitial } from '../agentTools/FederatedConnectionWizard.jsx';
import { isPluginMcp, pluginMcpNote } from '../../utils/pluginMcp.js';
import SecretConnectionWizard from '../agentTools/SecretConnectionWizard.jsx';
import usePluginConnectionTypes, { loadPluginConnectionTypes } from '../../hooks/usePluginConnectionTypes.js';
import ToolWorkspaceRegistryWizard from '../agentTools/ToolWorkspaceRegistryWizard.jsx';
import McpCredentialWizardDialog from '../mcp/McpCredentialWizardDialog.jsx';
import ToolSelectDialog from '../agentTools/ToolSelectDialog.jsx';
import PortalMenu from '../PortalMenu.jsx';
import DeleteConnectionDialog from './DeleteConnectionDialog.jsx';
import ConfirmDialog from '../ConfirmDialog.jsx';
import {
  canEditDeleteRegistrySecret,
  registrySecretInheritedHere,
} from '../../utils/secretsRegistryRowActions.js';

const TOOLS_ENV =
  (typeof import.meta !== 'undefined' &&
    (import.meta.env?.VITE_TOOLS_ENV || import.meta.env?.VITE_ENV)) ||
  'dev';

/** ``POST /tools/probe`` — no workspace network row; backend accepts tenant-only auth (see tools_impl). */
const REGISTRY_PROBE_NETWORK_ID = '__registry_probe__';

/** Hydrate wizard ``initialEntry`` from ``GET …/secrets-registry/{id}`` (full Dynamo row JSON). */
export function mapRegistrySecretToWizardInitial(secret) {
  if (!secret || typeof secret !== 'object') return undefined;
  const cfg = { ...(secret.config || {}) };
  const at = String(secret.authType || '').trim();
  if (at) cfg.AUTH_TYPE = cfg.AUTH_TYPE || at;
  if (!String(cfg.AUTH_TYPE || '').trim()) cfg.AUTH_TYPE = 'Bearer';
  let v = secret.version;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    const n = v != null ? Number(v) : NaN;
    v = Number.isFinite(n) ? n : undefined;
  }
  return {
    instanceId: 'ti_registry_existing',
    instanceLabel: String(secret.secretName || '').trim(),
    secretId: String(secret.secretId || '').trim(),
    secretName: secret.secretName,
    config: cfg,
    secretRefs: secret.secretRefs && typeof secret.secretRefs === 'object' ? { ...secret.secretRefs } : {},
    registryLogicalVersion: typeof v === 'number' ? v : undefined,
  };
}

/**
 * Mirrors ``tools_impl._normalise_input_schema`` so we can derive ``x-secret`` keys even when the
 * API omitted ``secretFields`` (hints are optional on some payloads).
 */
function normalizeCatalogInputSchema(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const inner = 'json' in raw ? raw.json : raw;
  return typeof inner === 'object' && inner !== null ? inner : {};
}

function inferSecretFieldKeysFromCatalogSchema(rawSchema) {
  const schema = normalizeCatalogInputSchema(rawSchema);
  const props = schema.properties;
  if (!props || typeof props !== 'object') return [];
  const out = [];
  for (const [key, spec] of Object.entries(props)) {
    if (spec && typeof spec === 'object' && spec['x-secret'] === true) {
      out.push(key);
    }
  }
  return out;
}

/** Built-in MCP tools whose Connections UX is wired in this package (credential registry). */
const REGISTRY_WIRED_TOOL_NAMES = new Set(['generic-rest-api', 'code-connector', 'cred-broker', 'workspace']);
// Friendly names for the DECLARED-connector synthetic rows (room agents' requiredConnectors).
const CONNECTOR_CLASS_LABELS = {
  'code-connector': 'Git connection (code-connector)',
  'cred-broker': 'Credential broker (cred-broker)',
  'generic-rest-api': 'REST API connection (generic-rest-api)',
};

// PLUGINS.md — every CONNECTION TYPE an installed plugin contributes is wired here too (its generic federated wizard)
loadPluginConnectionTypes().then((types) => {
  for (const [id, t] of Object.entries(types || {})) {
    REGISTRY_WIRED_TOOL_NAMES.add(id);
    if (!CONNECTOR_CLASS_LABELS[id]) CONNECTOR_CLASS_LABELS[id] = t.displayName || t.label || id;
  }
});

/** Full-width SECOND DECK under a row whose credential FAILED its last probe — shows the
 *  captured error verbatim so the fixer sees exactly why the fail-fast gate is blocking the
 *  trigger (agent active + cred connection probe BAD). Rendered as its own ``<tr>`` spanning
 *  all columns, directly beneath the (red-highlighted) row. ``null`` when there's no error. */
function ProbeErrorDeckRow({ message }) {
  const msg = String(message || '').trim();
  if (!msg) return null;
  return (
    <tr className="bg-red-50/70" data-testid="secrets-probe-error-deck">
      <td colSpan={8} className="border-b border-red-100 px-3 pb-2 pt-0 text-[11px] leading-snug text-red-700">
        <span className="font-medium">Last probe error:</span> {msg}
      </td>
    </tr>
  );
}

/** Inline "+ Add" on the Agents column: a PortalMenu multi-select of ROOM agents, greying the
 *  already-assigned ones; Save assigns the connection to the picked agents. ``roomAgents`` =
 *  ``[{id,label}]``; ``assignedIds`` = ids already on this row; ``onAdd(ids[])`` performs the write. */
function AddAgentsButton({ roomAgents = [], assignedIds = [], onAdd = null }) {
  const btnRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [picked, setPicked] = React.useState(() => new Set());
  const assigned = React.useMemo(() => new Set(assignedIds.map(String)), [assignedIds]);
  const available = React.useMemo(
    () => (Array.isArray(roomAgents) ? roomAgents : [])
      .map((a) => ({ id: String(a?.id || '').trim(), label: String(a?.label || a?.id || '').trim() }))
      .filter((a) => a.id && !assigned.has(a.id)),
    [roomAgents, assigned],
  );
  if (!onAdd || !Array.isArray(roomAgents) || roomAgents.length === 0) return null;
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const save = () => {
    const ids = [...picked];
    setOpen(false);
    setPicked(new Set());
    if (ids.length) onAdd(ids);
  };
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700"
        title="Assign this connection to more agents"
        data-testid="secrets-add-agents"
      >
        + Add
      </button>
      <PortalMenu
        anchorRef={btnRef}
        open={open}
        onClose={() => { setOpen(false); setPicked(new Set()); }}
        align="bottom-left"
        className="rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        <div className="max-h-56 min-w-[13rem] overflow-y-auto p-2 text-xs">
          {available.length === 0 ? (
            <p className="px-1 py-1.5 text-gray-500">All room agents already assigned.</p>
          ) : (
            <ul className="space-y-0.5">
              {available.map((a) => (
                <li key={a.id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-gray-50">
                    <input type="checkbox" checked={picked.has(a.id)} onChange={() => toggle(a.id)}
                      data-testid={`secrets-add-agent-opt:${a.id}`} />
                    <span className="truncate text-gray-800">{a.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5">
            <button
              type="button"
              disabled={picked.size === 0}
              onClick={save}
              className="rounded bg-gray-900 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-gray-700 disabled:opacity-40"
              data-testid="secrets-add-agents-save"
            >
              Add {picked.size || ''}
            </button>
          </div>
        </div>
      </PortalMenu>
    </>
  );
}

/** The "Agents" column cell — one connector : many agents on a SINGLE row (not one line
 *  per agent). Shows up to 3 agent chips + "+N", full roster in the tooltip. "—" when the
 *  row carries no agent set (MCP / registry rows). */
function AgentsCell({ agents, onRemove = null, onAdd = null, roomAgents = [] }) {
  // SCOPE ISOLATION: a connection is shared DOWN the hierarchy (tenant→network→room), but its
  // consumers are PER-SCOPE — only the CURRENT mount's own agents may appear. So filter the displayed
  // agents to this mount's roster (room → its seededRoster; network → the network agents); a
  // sibling-room or network-only agent must never show in this room's column. Empty roster = no
  // roster context (classic rooms, whose agents ARE the network's) → show all (back-compat).
  const rosterIds = Array.isArray(roomAgents) && roomAgents.length
    ? new Set(roomAgents.map((a) => String(a?.id || '').trim()).filter(Boolean))
    : null;
  const list = (Array.isArray(agents) ? agents : [])
    .map((a) => ({ id: String(a?.id || a?.label || '').trim(), label: String(a?.label || a?.id || '').trim() }))
    .filter((a) => a.label)
    .filter((a) => !rosterIds || rosterIds.has(a.id));
  const canAdd = Boolean(onAdd && Array.isArray(roomAgents) && roomAgents.length);
  const assignedIds = list.map((a) => a.id);
  if (!list.length) {
    // No agents yet — still offer "+ Add" (assign the first agent) when supported.
    return (
      <td className="px-3 py-2 text-[11px] text-gray-400" data-testid="secrets-agents-cell">
        {canAdd ? <AddAgentsButton roomAgents={roomAgents} assignedIds={assignedIds} onAdd={onAdd} /> : '—'}
      </td>
    );
  }
  const shown = list.slice(0, 3);
  const extra = list.length - shown.length;
  return (
    <td className="px-3 py-2 text-[11px] text-gray-700" title={list.map((a) => a.label).join(', ')}
      data-testid="secrets-agents-cell">
      <span className="flex flex-wrap items-center gap-1">
        {shown.map((a, i) => (
          <span key={a.id || i}
            className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700">
            {a.label}
            {onRemove ? (
              <button
                type="button"
                onClick={() => onRemove(a.id)}
                className="ml-0.5 rounded px-0.5 font-semibold leading-none text-gray-400 hover:bg-gray-200 hover:text-red-600"
                title={`Remove this connection from ${a.label}`}
                aria-label={`Remove from ${a.label}`}
                data-testid={`secrets-agent-remove:${a.id}`}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {extra > 0 ? <span className="text-[10px] font-medium text-gray-500">+{extra}</span> : null}
        {canAdd ? <AddAgentsButton roomAgents={roomAgents} assignedIds={assignedIds} onAdd={onAdd} /> : null}
      </span>
    </td>
  );
}

/** Curated "Most used" highlights for the Create-New picker. Surfaced as a
 *  badge + sort boost; purely cosmetic, safe to tune as usage data lands. */
const MOST_USED_MCPS = new Set(['github', 'slack', 'postgres', 'fetch']);
const MOST_USED_CONNECTORS = new Set(['generic-rest-api', 'code-connector', 'workspace']);

/** Synthetic catalog tile for ``workspace`` (doc/arch/ASSETS.md). Unlike
 *  connectors there is no backing Lambda — shared workspaces are metadata + S3
 *  objects — so the tile is injected client-side rather than coming from
 *  ``listTools``. Shape matches enough of a catalog row for ToolSelectDialog +
 *  the picker dispatch. (Distinct from the read-only Data Plane Asset.) */
const WORKSPACE_PSEUDO_TOOL = {
  name: 'workspace',
  toolName: 'workspace',
  toolType: 'service',
  displayName: 'Shared workspace',
  description: 'Shared, mutable workspace — upload a zip/tar of starter files; '
    + 'attach it to builder agents that mutate it in place. Reusable across runs and rooms.',
};

/** Tools eligible for Connections — ``service`` + credentials (API hints or schema walk, or wired tools). */
export function toolSupportsRegistryConnections(tool) {
  if (!tool || typeof tool !== 'object') return false;
  const tt = String(tool.toolType || '').trim().toLowerCase();
  if (tt !== 'service') return false;
  const name = String(tool.name || '').trim();
  const fromApi = Array.isArray(tool.secretFields)
    ? tool.secretFields.filter((k) => String(k || '').trim())
    : [];
  const inferred = inferSecretFieldKeysFromCatalogSchema(tool.inputSchema);
  const hinted = Boolean(fromApi.length || inferred.length);

  /** Primary: catalog lists ``secretFields`` or we infer ``x-secret`` from schema. */
  if (hinted) return true;
  /** Fall back: IaC-stable tools whose registry wizards ship here (hints may be missing on truncated rows). */
  return REGISTRY_WIRED_TOOL_NAMES.has(name);
}

/** Row visible when attaching or filtering within a network context */
export function registrySecretVisibleForNetwork(row, catalogNetworkId) {
  if (!catalogNetworkId || catalogNetworkId === 'new') return true;
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'TENANT') return true;
  if (k === 'NETWORK') {
    const nid = String(s.networkId ?? s.network_id ?? '').trim();
    return nid === String(catalogNetworkId).trim();
  }
  return false;
}

function formatScope(row) {
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'NETWORK') return `Network · ${String(s.networkId ?? s.network_id ?? '—')}`;
  if (k === 'POOL') {
    // POOL scope IS the room-wide tier (every agent in the room). Show it as
    // "Room Wide" — the same wording the room settings drawer uses — rather
    // than the internal "Pool · net / pool" coordinates.
    return 'Room Wide (all agents)';
  }
  return 'Tenant-wide';
}

function formatAuthType(row) {
  const a = row.authType;
  if (a == null || String(a).trim() === '') return '—';
  return String(a);
}

/** Explains why Edit/Delete are disabled for inherited / higher-layer registry rows. */
function registryRowMutateHint(row, { allowTenantRegistryMutate }) {
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'TENANT' && !allowTenantRegistryMutate) {
    return 'Tenant-wide connections are managed under Workspace → Settings → Connections (owners and admins only).';
  }
  if (k === 'TENANT') {
    return 'Tenant-wide connections can only be changed by a workspace owner or admin.';
  }
  if (k === 'NETWORK') {
    return 'Network-scoped connections can only be changed by the network designer.';
  }
  if (k === 'POOL') {
    return 'Pool-scoped connections can only be changed by this pool’s creator.';
  }
  return 'You do not have permission to change this connection.';
}

/** Is an MCP-credential row INHERITED at the current mount (i.e. its scope is
 *  broader than where the catalog is mounted)? Inherited rows are read-only;
 *  the user can "Override here" to create a more-specific row. Mount scope is
 *  derived from catalogNetworkId/catalogPoolId (TENANT < NETWORK < POOL). */
function mcpRowIsInherited(row, { catalogNetworkId, catalogPoolId }) {
  const k = String(row?.scope?.kind || 'TENANT').toUpperCase();
  const haveNet = Boolean(catalogNetworkId && catalogNetworkId !== 'new');
  const havePool = Boolean(catalogPoolId);
  // POOL mount: TENANT + NETWORK rows are inherited; POOL row is own.
  if (haveNet && havePool) return k !== 'POOL';
  // NETWORK mount: TENANT row is inherited; NETWORK row is own.
  if (haveNet) return k !== 'NETWORK';
  // TENANT mount: nothing is inherited.
  return false;
}

function RegistryTestStatusBadge({ row }) {
  const raw = String(row.testStatus || 'UNTESTED').toUpperCase();
  let label = 'Not tested';
  let cls = 'border-amber-200 bg-amber-50 text-amber-900';
  if (raw === 'TESTED_OK') {
    label = 'Test OK';
    cls = 'border-emerald-200 bg-emerald-50 text-emerald-900';
  } else if (raw === 'TESTED_FAIL') {
    label = 'Test error';
    cls = 'border-red-200 bg-red-50 text-red-900';
  }
  const tip = row.lastTestError ? String(row.lastTestError) : undefined;
  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      title={tip}
    >
      {label}
    </span>
  );
}

/** Extra lines for the registry test dialog — generic-rest MCP probe shape only for now. */
function registryProbeSummaryLines(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const probe = payload.result && typeof payload.result === 'object' ? payload.result : null;
  if (!probe) return [];
  const lines = [];
  if (typeof probe.status === 'number') lines.push(`Upstream HTTP status: ${probe.status}`);
  if (probe.method != null || probe.url != null) {
    const m = probe.method != null ? String(probe.method) : '';
    const u = probe.url != null ? String(probe.url) : '';
    lines.push(`Request: ${[m, u].filter(Boolean).join(' ')}`.trim());
  }
  if (typeof probe.durationMs === 'number') lines.push(`Duration: ${probe.durationMs} ms`);
  const h = probe.headers && typeof probe.headers === 'object' ? probe.headers : {};
  const keys = ['Location', 'WWW-Authenticate', 'X-Seraph-Loginreason', 'X-Request-Id', 'X-Arequestid'];
  for (const k of keys) {
    const v = h[k] ?? h[k.toLowerCase()];
    if (v != null && String(v).trim() !== '') lines.push(`${k}: ${String(v)}`);
  }
  return lines;
}

function RegistryProbeResultDialog({ open, title, payload, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const lines = registryProbeSummaryLines(payload);
  let pretty = '';
  try {
    pretty = JSON.stringify(payload ?? {}, null, 2);
  } catch {
    pretty = String(payload);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="registry-probe-dialog-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <h3 id="registry-probe-dialog-title" className="text-sm font-semibold text-gray-900">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
        <div className="max-h-[calc(85vh-8rem)] overflow-y-auto px-4 py-3 text-xs text-gray-800">
          <div className="mb-3 space-y-1 rounded border border-gray-100 bg-gray-50 p-2">
            <div>
              <span className="font-semibold text-gray-700">Stored result: </span>
              <span className="font-mono text-[11px]">{String(payload?.testStatus || '—')}</span>
              {payload?.lastTestedAt ? (
                <span className="ml-2 text-[11px] text-gray-500">{String(payload.lastTestedAt)}</span>
              ) : null}
            </div>
            {payload?.lastTestError ? (
              <p className="text-[11px] leading-snug text-red-800">{String(payload.lastTestError)}</p>
            ) : null}
            {payload?.message && String(payload.message) !== String(payload.lastTestError || '') ? (
              <p className="text-[11px] leading-snug text-gray-700">{String(payload.message)}</p>
            ) : null}
            {lines.length ? (
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-[11px] text-gray-700">
                {lines.map((t, i) => (
                  <li key={`${i}-${t.slice(0, 64)}`} className="break-words">
                    {t}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">Full response</p>
          <pre className="max-h-[40vh] overflow-auto rounded border border-gray-100 bg-gray-900/95 p-2 font-mono text-[10px] leading-relaxed text-gray-100">
            {pretty}
          </pre>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function WhereUsedMenu({ secretId }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refsErr, setRefsErr] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setRefsErr('');
      try {
        const out = await getSecretsRegistryReferences(secretId);
        if (!cancelled) setRefs(Array.isArray(out.references) ? out.references : []);
      } catch (e) {
        if (!cancelled) setRefsErr(e?.message || 'Could not load references');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, secretId]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
        data-testid={`secrets-where-used-${secretId}`}
      >
        Where used
      </button>
      <PortalMenu
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        menuRole="region"
        align="bottom-right"
        className="max-w-sm rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        <div className="max-h-52 min-w-[14rem] overflow-y-auto p-2 text-xs text-gray-800">
          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : refsErr ? (
            <p className="text-red-600">{refsErr}</p>
          ) : refs.length === 0 ? (
            <p className="text-gray-500">Not used by any agent yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {refs.map((r, idx) => (
                <li key={`${r.networkId}-${r.agentId}-${r.instanceId}-${idx}`} className="leading-snug">
                  <span className="font-mono text-[11px] text-gray-900">{String(r.networkId || '—')}</span>
                  <span className="text-gray-400"> · </span>
                  <span className="font-mono text-[11px]">{String(r.agentId || '—')}</span>
                  {r.toolName ? (
                    <>
                      <span className="text-gray-400"> · </span>
                      <span className="text-gray-600">{String(r.toolName)}</span>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </PortalMenu>
    </>
  );
}

// NOTE: the tool-selection grid (ToolGlyph / ToolCard / tabbed picker) now
// lives in the shared ``ToolSelectDialog`` so Connections, Workbench, and the
// Room drawer present an IDENTICAL view + tool list. See agentTools/ToolSelectDialog.jsx.


/**
 * @param {{
 *   catalogNetworkId?: string,
 *   catalogPoolId?: string,
 *   tenantId: string,
 *   title?: string,
 *   subtitle?: string,
 *   allowCreate?: boolean,
 *   allowTenantRegistryMutate?: boolean,
 *   workspaceRole?: string,
 *   poolRole?: string,
 *   viewerUserSub?: string,
 *   networkOwnerUserId?: string,
 * }} props
 */
/** PLUGINS.md §3 — a plugin connection type's wizard follows its ``auth``: ``secret`` → the key/token wizard, else federated. */
function PluginConnectionWizard(props) {
  return props.spec?.auth === 'secret' ? <SecretConnectionWizard {...props} /> : <FederatedConnectionWizard {...props} />;
}

export default function SecretsCatalog({
  catalogNetworkId = '',
  catalogPoolId = '',
  // §13: when set, this room is LINKED to that Lead — connections whose POOL scope is the Lead's
  // are SHARED (badge "Shared", the peer of network "Inherited"). One rule for every row kind.
  leadPoolId = '',
  tenantId,
  title = 'Connections',
  subtitle = null,
  allowCreate = true,
  allowTenantRegistryMutate = false,
  workspaceRole = '',
  poolRole = '',
  viewerUserSub = '',
  networkOwnerUserId = '',
  // One-shot deep-link: when the user clicks "Create in Connections" from the
  // agent tool picker's empty instance list, the host switches to this tab and
  // passes the tool here ('workspace' | an MCP id) so we auto-open its creator.
  // ``onAutoOpenConsumed`` is called once we've acted, so it fires only once.
  autoOpenTool = null,
  onAutoOpenConsumed = null,
  // Round-trip: after a connection is CREATED here, notify the host so it can
  // return the user to the tool picker and auto-assign it. Shape:
  // ``({ tool, secretId, secretName, scope }) => void`` where ``tool`` is
  // 'workspace' | the MCP id | the connector toolName. Optional.
  onConnectionCreated = null,
  // READINESS (fail-fast framework): ``{ mcpId: { status:'OK'|'BAD'|'UNKNOWN', reason } }`` from the
  // room's MCP status fetch. When a row's credential is set but server-verified as BAD, the Status
  // cell shows a RED "Invalid" badge + the reason (highlighted row) so the user knows exactly what to
  // fix + re-test. Empty/absent → the plain Configured/Needs-credential badges (no readiness surface,
  // e.g. tenant/network catalog without a room context). Optional.
  readinessByMcp = null,
  // DECLARED CONNECTORS (room context): ``[{ connectorClass, needs:[], agentIds:[], configured }]``
  // from the room's MCP-status fetch — the connectors the room's agents DECLARE (code-connector /
  // cred-broker / generic-rest-api), resolved LIVE. Rendered as synthetic "Configure + Test" rows so
  // a room whose agents need a connector shows it here even before any credential row exists (the old
  // gap: a connector-heavy room's tab looked empty). Deduped against real registry rows. Optional.
  declaredConnectors = [],
  // DECLARED MCPs (room context): ``[{ mcpId, name, configured, agents:[{id,label}] }]`` — the
  // auth-required MCPs the room's agents enable. Populates the Agents column on real MCP rows AND
  // synthesizes a "Configure + Test" row for an enabled MCP that has no credential row yet (the gap
  // where a room full of GitLab-enabled agents showed no MCP at all). Optional.
  declaredMcps = [],
  // CONNECTOR ASSIGNMENTS (room context): ``[{ toolName, secretId, agents:[{id,label}], poolWide }]``
  // — human-assigned connector→agent bindings (pool overrides). Populates the Agents column on the
  // connector registry row bound by each, so the tab shows which agents use a connector in this room.
  // Optional.
  connectorAssignments = [],
  // Called after an agent-chip ✕/+Add mutates a connection's agents (room-scoped). The room wires
  // this to re-fetch its status so declaredMcps / connectorAssignments refresh. Optional.
  onConnectionsChanged = null,
  // Full room agent roster ``[{id,label}]`` for the "+ Add" selector (greys already-assigned). From
  // the room's manifest roster. Empty → no "+ Add" affordance. Optional.
  roomAgents = [],
}) {
  // §13: ONE rule for "this connection is shared from the Lead", across every row kind (MCP,
  // connector, registry). The backend stamps ``sharedFromLeadPool`` on registry rows; any row whose
  // POOL scope IS the Lead's pool also qualifies (covers the MCP-list rows, which aren't stamped).
  // Drives the "Shared" badge — the peer of the network "Inherited" badge.
  const isSharedFromLead = (row) => {
    const lead = String(leadPoolId || '').trim();
    if (!lead || !row) return false;
    if (row.sharedFromLeadPool) return true;
    const sc = row.scope;
    const poolId = (sc && typeof sc === 'object')
      ? String(sc.poolId ?? sc.pool_id ?? '')
      : (String(sc || '').toUpperCase() === 'POOL' ? String(row.scopeRef ?? '') : '');
    return !!poolId && poolId === lead;
  };
  // PLUGINS.md — the installed plugins' connection types ({id: {label, wizard, …}}); above the flag return (hooks order)
  const pluginTypes = usePluginConnectionTypes();
  const [federatedType, setFederatedType] = useState('');
  if (!secretsRegistryV1Enabled()) {
    return null;
  }

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // Read-only "View" of a workspace row (files + baked template/skill versions).
  const [viewingWorkspaceId, setViewingWorkspaceId] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [authFilter, setAuthFilter] = useState('');
  const [genericRestTool, setGenericRestTool] = useState(null);
  /** Tool catalog row for ``code-connector`` — passed into the registry wizard (labels, future hints). */
  const [codeConnectorTool, setCodeConnectorTool] = useState(null);
  /** Subset of MCP tools with stored credentials (``service`` + ``secretFields``). */
  const [connectionTools, setConnectionTools] = useState([]);
  // FULL tool inventory from GET /tools (all types incl system + injected natives) — the Create-New
  // surface renders EVERY tool by its availability (explicit-only; nothing hidden).
  const [allTools, setAllTools] = useState([]);
  const [toolChoices, setToolChoices] = useState([]);
  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  /** ``rest`` · ``ToolGenericRestConfigWizard``; ``codeconnector`` · ``ToolCodeConnectorRegistryWizard``. */
  const [wizardKind, setWizardKind] = useState(null);
  /** When set, wizard opens in edit mode with this ``initialEntry`` shape. */
  const [wizardInitial, setWizardInitial] = useState(undefined);
  /** Authenticated MCPs from the platform registry (id, displayName,
   *  credentialFields, credentialUi) — drives the MCP picker + wizard. */
  const [mcpRegistry, setMcpRegistry] = useState([]);
  /** Configured MCP credential rows (scoped) merged into the catalog list. */
  const [mcpRows, setMcpRows] = useState([]);
  /** When set, the MCP credential wizard (McpCredentialForm) is open for this
   *  MCP at ``registryScope`` (create) or an explicit override scope. */
  const [mcpWizard, setMcpWizard] = useState(null); // { mcp, scope }
  const [mcpBusyKey, setMcpBusyKey] = useState(''); // `${mcpId}:${scopeKind}` mid delete
  const [editLoadingSecretId, setEditLoadingSecretId] = useState('');
  const [testingSecretId, setTestingSecretId] = useState('');
  const [probeBanner, setProbeBanner] = useState('');
  const [deletingSecretId, setDeletingSecretId] = useState('');
  /** Type-to-confirm delete: holds {secretId, name, isWorkspace} for the open dialog. */
  const [pendingDelete, setPendingDelete] = useState(null);
  // Unified Connections surfacing (framework Phase 4): every connection visible at/below this mount.
  // We use ONLY the INHERITED (network/tenant) ones — surfaced read-only so "nothing is unnoticed" —
  // and leave the tab's own row-building untouched. Best-effort; a fetch miss just shows nothing extra.
  const [surfacedConns, setSurfacedConns] = useState([]);
  useEffect(() => {
    const nid = String(catalogNetworkId || '').trim();
    if (!nid) { setSurfacedConns([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await listConnections({ networkId: nid, poolId: String(catalogPoolId || '').trim() || undefined });
        if (!cancelled) setSurfacedConns(Array.isArray(r?.connections) ? r.connections : []);
      } catch {
        if (!cancelled) setSurfacedConns([]);
      }
    })();
    return () => { cancelled = true; };
  }, [catalogNetworkId, catalogPoolId]);
  // Agent-chip ✕ removal is confirmed via the house ConfirmDialog (window.confirm is banned
  // house-wide). Holds {row, agentId, what, isMcp} for the open dialog; ``removingAgent`` is the
  // in-flight flag so the dialog can show progress + block a double submit.
  const [pendingAgentRemoval, setPendingAgentRemoval] = useState(null);
  const [removingAgent, setRemovingAgent] = useState(false);
  /** Row test: show probe payload (upstream status is authoritative; API HTTP 200 alone is not). */
  const [registryProbeDialog, setRegistryProbeDialog] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 280);
    return () => clearTimeout(t);
  }, [query]);

  const bootstrapTools = useCallback(async () => {
    try {
      // FULL inventory (no forAgent) — the Create-New surface shows EVERY tool the platform knows:
      // connectors + internet-search (active) + coming-soon natives + system tools (e.g. s3-explorer),
      // each rendered by its availability (explicit-only, nothing hidden).
      const { tools } = await listTools();
      const safe = Array.isArray(tools) ? tools : [];
      const withSecrets = safe.filter((t) => toolSupportsRegistryConnections(t));
      withSecrets.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
      setConnectionTools(withSecrets);
      setAllTools(safe.slice().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))));
      setGenericRestTool(safe.find((t) => t.name === 'generic-rest-api') || null);
      setCodeConnectorTool(safe.find((t) => t.name === 'code-connector') || null);
      setToolChoices([
        { value: '', label: 'All tools' },
        ...safe.filter((t) => t?.name).map((t) => ({ value: t.name, label: t.name })),
      ]);
    } catch {
      setConnectionTools([]);
      setAllTools([]);
      setGenericRestTool(null);
      setCodeConnectorTool(null);
      setToolChoices([{ value: '', label: 'All tools' }]);
    }
    // FULL MCP catalog — the selection view is identical across Connections /
    // Workbench / Room (same tool list + counts). We no longer pre-filter to
    // authRequired here; an unauthenticated MCP (no credential to save) is shown
    // but its pick is handled with a friendly "no setup needed" message rather
    // than an empty wizard (see handlePickConnectionTool).
    try {
      const { mcps } = await listMcpRegistry();
      setMcpRegistry(Array.isArray(mcps) ? mcps : []);
    } catch {
      setMcpRegistry([]);
    }
  }, []);

  const refresh = useCallback(
    async (silent) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError('');
      try {
        // Pass the network + pool so the backend returns the correct
        // inheritance chain for this surface: pool → its own POOL rows + parent
        // NETWORK + TENANT; network → NETWORK + TENANT; tenant → TENANT only.
        // Without poolId the backend EXCLUDES POOL-scoped rows entirely, so a
        // room-scoped connection created here would never appear in its own
        // Connections tab. (Bug: room-scoped 'test-ws2' was invisible because
        // these were omitted + a client filter dropped POOL rows.)
        const { secrets: raw } = await listSecretsRegistry({
          toolName: toolFilter || undefined,
          q: debouncedQ || undefined,
          networkId: catalogNetworkId && catalogNetworkId !== 'new' ? catalogNetworkId : undefined,
          poolId: catalogPoolId || undefined,
        });
        let list = Array.isArray(raw) ? raw : [];
        // Skills (toolName='skill') share the secrets-registry table but are NOT
        // connections — they have their own Skills tab (doc/arch/vertical/SKILLS.md). The
        // registry list returns every tool type, so drop skill rows here so they
        // never appear in Connections. (Workspaces DO belong here — they have a
        // synthetic create tile above — so only 'skill' is excluded.)
        list = list.filter((r) => String(r?.toolName || '') !== 'skill');
        // NOTE: no client-side scope filter — the backend already returns exactly
        // the inheritance chain for (networkId, poolId). The old
        // ``registrySecretVisibleForNetwork`` pass DROPPED POOL rows (it had no
        // pool context) and is why room-scoped rows vanished.
        setRows(list);
      } catch (e) {
        setError(e?.message || 'Could not load connections');
        setRows([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
      // MCP credential rows (scoped) — merged into the same table downstream.
      // Best-effort: a failure here must not blank the connector list.
      try {
        const { mcpCredentials } = await listMcpCredentials({
          networkId: catalogNetworkId && catalogNetworkId !== 'new' ? catalogNetworkId : undefined,
          poolId: catalogPoolId || undefined,
        });
        setMcpRows(Array.isArray(mcpCredentials) ? mcpCredentials : []);
      } catch {
        setMcpRows([]);
      }
    },
    [toolFilter, debouncedQ, catalogNetworkId, catalogPoolId],
  );

  useEffect(() => {
    bootstrapTools();
  }, [bootstrapTools]);

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  useEffect(() => {
    const onRegistrySignal = () => {
      refresh(true);
    };
    window.addEventListener('stepflow-secret-registry-changed', onRegistrySignal);
    return () => window.removeEventListener('stepflow-secret-registry-changed', onRegistrySignal);
  }, [refresh]);

  const registryScope = useMemo(() => {
    const nid = String(catalogNetworkId || '').trim();
    const pid = String(catalogPoolId || '').trim();
    if (nid && nid !== 'new' && pid) {
      return { kind: 'POOL', networkId: nid, poolId: pid };
    }
    if (nid && nid !== 'new') {
      return { kind: 'NETWORK', networkId: nid };
    }
    return { kind: 'TENANT' };
  }, [catalogNetworkId, catalogPoolId]);

  const authTypeChoices = useMemo(() => {
    const labels = [];
    let hasUnset = false;
    for (const r of rows) {
      const t = String(r.authType || '').trim();
      if (!t) hasUnset = true;
      else if (!labels.includes(t)) labels.push(t);
    }
    labels.sort((a, b) => a.localeCompare(b));
    const out = [{ value: '', label: 'All auth types' }];
    if (hasUnset) out.push({ value: '__none__', label: '— (catalog default)' });
    for (const v of labels) out.push({ value: v, label: v });
    return out;
  }, [rows]);

  const mcpById = useMemo(() => {
    const m = {};
    for (const r of mcpRegistry) m[r.id] = r;
    return m;
  }, [mcpRegistry]);

  // Normalize MCP credential rows into the connector table's row shape so they
  // render in the SAME table. ``kind:'mcp'`` flags the actions cell to use the
  // MCP wizard/delete/test instead of the connector ones.
  const mcpRowsNormalized = useMemo(
    () => mcpRows.map((r) => ({
      kind: 'mcp',
      mcpId: r.mcpId,
      instanceId: r.instanceId || 'default',
      label: r.label || '',
      // synthesized stable key — include instanceId so multiple labeled
      // instances of the same MCP at one scope each get their own row.
      secretId: `mcp:${r.mcpId}:${r.instanceId || 'default'}:${r.scope?.kind || 'TENANT'}:${r.scopeId || ''}`,
      // NAME column = the label (the instance name), aligning with connectors
      // where NAME is the secret's name; the tool/MCP id lives in the Tool column.
      // Fall back to the MCP display name for legacy "default" rows with no label.
      secretName: r.label || r.displayName || r.mcpId,
      toolName: `mcp:${r.mcpId}`,
      authType: '',
      scope: r.scope || { kind: 'TENANT' },
      updatedAt: r.setAt || '',
      // PRESERVE the backend's ``configured`` flag. It's ``true`` iff the row's ``secretRefs``
      // actually carries credential pointers (``mcp_creds_impl.py::list_scoped…``, ~L2019). An
      // empty shell (seeded/backfilled with ``{}``) is FALSE — without this pass-through the row's
      // render sees ``undefined`` and hits the "row.configured === false" strict check → falsely
      // shows the green "Configured" badge on an empty shell. Coerce to a real boolean.
      configured: r.configured === true,
      _mcpInherited: mcpRowIsInherited(r, { catalogNetworkId, catalogPoolId }),
    })),
    [mcpRows, catalogNetworkId, catalogPoolId],
  );

  // Synthetic rows for connectors the room's agents DECLARE but that have no real
  // registry row yet — so a room needing a connector always shows it (with live
  // configured-status) + a Configure action. Deduped against real registry rows by
  // toolName (a real row wins — it carries the actual secret + test status). Skipped
  // when there's no room context (declaredConnectors only comes from the room fetch).
  const declaredConnectorRows = useMemo(() => {
    if (!Array.isArray(declaredConnectors) || declaredConnectors.length === 0) return [];
    const realToolNames = new Set(
      (rows || []).map((r) => String(r.toolName || '').trim()).filter(Boolean),
    );
    const out = [];
    for (const d of declaredConnectors) {
      const cls = String(d?.connectorClass || '').trim();
      if (!cls || realToolNames.has(cls)) continue;   // real row already shows it
      out.push({
        kind: 'connector-declared',
        toolName: cls,
        connectorClass: cls,
        configured: d?.configured === true,
        needs: Array.isArray(d?.needs) ? d.needs : [],
        agentIds: Array.isArray(d?.agentIds) ? d.agentIds : [],
        // [{id,label}] — the agents that need this connector (rendered on one row).
        agents: Array.isArray(d?.agents) ? d.agents : [],
        secretId: `declared:${cls}`,
      });
    }
    return out;
  }, [declaredConnectors, rows]);

  // secretId → the agents a human bound this connector to (pool overrides), so a connector
  // registry row shows which room agents use it. Also key by toolName for pool-wide binds that
  // point at an inherited/tenant secret whose id may not match a listed row 1:1.
  const connectorAgentsBySecretId = useMemo(() => {
    const bySid = {};
    for (const a of (Array.isArray(connectorAssignments) ? connectorAssignments : [])) {
      const sid = String(a?.secretId || '').trim();
      const agents = Array.isArray(a?.agents) ? a.agents : [];
      if (sid) bySid[sid] = agents;
    }
    return bySid;
  }, [connectorAssignments]);

  // (mcpId, scope) → its consuming agents [{id,label}], so a real MCP row shows the agents that use
  // THAT shell's scope. Scope-keyed on purpose: a Network-scope row and the room's own Pool-scope row
  // are DIFFERENT connections — the network row must show its own consumers (none, when the room uses
  // its own copy), not the pool's. Key ``"{mcpId}:{SCOPE}"`` (SCOPE ∈ POOL|NETWORK|TENANT).
  const mcpAgentsByKey = useMemo(() => {
    const out = {};
    for (const d of (Array.isArray(declaredMcps) ? declaredMcps : [])) {
      const mid = String(d?.mcpId || '').trim();
      if (!mid) continue;
      const sc = String(d?.scope || '').toUpperCase();
      out[`${mid}:${sc}`] = Array.isArray(d?.agents) ? d.agents : [];
    }
    return out;
  }, [declaredMcps]);

  // Synthetic rows for MCPs the room's agents enable but that have no credential row yet —
  // mirror of declaredConnectorRows. Deduped against real MCP rows by mcpId.
  const declaredMcpRows = useMemo(() => {
    if (!Array.isArray(declaredMcps) || declaredMcps.length === 0) return [];
    // Dedup against real credential rows by (mcpId, SCOPE): a real catalog row already renders that
    // shell (Room-Wide POOL, Network, Tenant) with Edit/Remove — so the declared row for the SAME
    // (mcp, scope) must NOT double it. declaredMcps only ADDS rows for a shell the catalog doesn't
    // return (e.g. an MCP agents enable that has NO credential row anywhere → "needs credential").
    const realMcpKeys = new Set(
      (mcpRowsNormalized || [])
        .map((r) => `${String(r.mcpId || '').trim()}:${String(r.scope?.kind || '').toUpperCase()}`),
    );
    const realMcpIds = new Set(
      (mcpRowsNormalized || []).map((r) => String(r.mcpId || '').trim()).filter(Boolean),
    );
    const out = [];
    for (const d of declaredMcps) {
      const mid = String(d?.mcpId || '').trim();
      if (!mid) continue;
      const scopeId = String(d?.scopeId || '').trim();
      const scopeKind = String(d?.scope || '').toUpperCase();
      // Real catalog row already renders this shell → don't double it. Scoped declared dedups by
      // (mcpId, scope); a scopeless declared (no resolved scope) dedups by mcpId (any real row wins).
      if (realMcpKeys.has(`${mid}:${scopeKind}`) || (!scopeKind && realMcpIds.has(mid))) continue;
      out.push({
        kind: 'mcp-declared',
        mcpId: mid,
        scope: d?.scope || null,
        scopeId: scopeId || null,
        // AGENT-scoped shells are 1:1 — the single agent that owns this distinct connection.
        agentId: d?.agentId || null,
        toolName: `mcp:${mid}`,
        name: d?.name || mid,
        configured: d?.configured === true,
        agents: Array.isArray(d?.agents) ? d.agents : [],
        // Distinct key per SHELL so 6 per-agent gitlab connections render as 6 rows (not one).
        secretId: `declared-mcp:${mid}${scopeId ? `:${scopeId}` : ''}`,
      });
    }
    return out;
  }, [declaredMcps, mcpRowsNormalized]);

  const filteredRows = useMemo(() => {
    let connectorRows = rows;
    if (authFilter === '__none__') connectorRows = rows.filter((r) => !String(r.authType || '').trim());
    else if (authFilter) connectorRows = rows.filter((r) => String(r.authType || '').trim() === authFilter);
    // Auth filter is connector-only; MCP rows always appended (auth N/A).
    // ``toolFilter`` for mcp:* names still narrows them.
    let mcp = mcpRowsNormalized;
    if (toolFilter) mcp = mcp.filter((r) => r.toolName === toolFilter);
    // Declared-but-unconfigured connector + MCP rows honor the tool filter too.
    let declared = declaredConnectorRows;
    if (toolFilter) declared = declared.filter((r) => r.toolName === toolFilter);
    let declaredMcpR = declaredMcpRows;
    if (toolFilter) declaredMcpR = declaredMcpR.filter((r) => r.toolName === toolFilter);
    const own = [...connectorRows, ...mcp, ...declaredMcpR, ...declared];
    // Append INHERITED (network/tenant) connections read-only, deduped vs the tab's own rows by
    // (kind, tool) — so a connection defined higher in the hierarchy is visible here but never
    // duplicates an own row. Additive: does not alter any existing row.
    const shownKeys = new Set(own.map((r) => (
      r.mcpId
        ? connectionDedupKey('mcp', r.mcpId)
        : connectionDedupKey('connector', r.connectorClass || r.toolName || '')
    )));
    let inherited = inheritedConnectionRows(surfacedConns, shownKeys);
    if (toolFilter) {
      inherited = inherited.filter((r) => r.toolId === toolFilter || `mcp:${r.toolId}` === toolFilter);
    }
    return [...own, ...inherited];
  }, [rows, authFilter, mcpRowsNormalized, toolFilter, declaredConnectorRows, declaredMcpRows, surfacedConns]);

  const connectionToolNameSet = useMemo(
    () => new Set(connectionTools.map((t) => String(t?.name || '').trim()).filter(Boolean)),
    [connectionTools],
  );

  const closeWizards = useCallback(() => {
    setWizardKind(null);
    setWizardInitial(undefined);
    setMcpWizard(null);
  }, []);

  // Agent-chip ✕: remove ONE connection from ONE agent, room-scoped. Routed by row kind — an MCP
  // (declared or configured) → per-agent MCP disable; a connector registry row → clear that agent's
  // pool connector override. On success, ask the room to re-fetch (declaredMcps/connectorAssignments
  // are its props) and refresh our own rows. Confirms first (it changes run behavior).
  const handleRemoveAgent = useCallback((row, agentId) => {
    const aid = String(agentId || '').trim();
    if (!aid || !row) return;
    const isMcp = row.kind === 'mcp' || row.kind === 'mcp-declared';
    const what = isMcp ? String(row.mcpId || '') : String(row.toolName || row.connectorClass || '');
    // Open the house confirm dialog; the actual removal runs in ``confirmRemoveAgent``.
    setPendingAgentRemoval({ row, agentId: aid, what, isMcp });
  }, []);

  // Confirmed removal of ONE connection from ONE agent (routed by row kind). MCP → per-agent state
  // 'off'; connector → clear the agent's pool override. On success, ask the room to re-fetch
  // (declaredMcps/connectorAssignments are its props) and refresh our own rows.
  const confirmRemoveAgent = useCallback(async () => {
    const p = pendingAgentRemoval;
    if (!p || !p.row) return;
    const aid = String(p.agentId || '').trim();
    const nid = String(catalogNetworkId || '').trim();
    const pid = String(catalogPoolId || '').trim();
    if (!aid || !nid) { setPendingAgentRemoval(null); return; }
    setRemovingAgent(true);
    try {
      if (!pid) {
        // NETWORK scope (no pool): edit the agent's BASE enabledMcpIds — the network IS the base,
        // there's no room override to write. (Connector base-edits at network scope aren't wired yet.)
        if (p.isMcp) {
          await setAgentNetworkMcpEnablement(nid, aid, p.row.mcpId, false);
        } else {
          setError('Removing a connector at the network scope isn’t supported yet — edit it in the agent’s tools.');
          setPendingAgentRemoval(null);
          setRemovingAgent(false);
          return;
        }
      } else if (p.isMcp) {
        await setAgentMcpState(nid, pid, aid, p.row.mcpId, 'off');
      } else {
        // CONNECTOR ✕ → write a CONNSTATE 'off' (NOT just clear the credential override): the
        // architect assignment lives in the frozen release tier, so only an 'off' state suppresses
        // it across the tab, the agent drawer, AND the run. Every surface subtracts 'off'.
        await setAgentConnectorState(nid, pid, aid, p.row.toolName, 'off');
      }
      onConnectionsChanged?.();  // room/network re-fetches status → chips update
      // Broadcast so sibling surfaces (room drawer, agent tools editor) reflect this same-row change.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed', { detail: { source: 'connections-tab' } }));
      }
      refresh(true);
      setPendingAgentRemoval(null);
    } catch (e) {
      setError(e?.message || 'Could not remove the connection from that agent');
    } finally {
      setRemovingAgent(false);
    }
  }, [pendingAgentRemoval, catalogNetworkId, catalogPoolId, onConnectionsChanged, refresh]);

  // Agent-chip "+ Add": assign this connection to MORE room agents. Routed by row kind — MCP →
  // per-agent MCP state 'on' (adds an MCP the architect never gave); connector → bind the row's secret
  // (real registry row) or enable it (declared row). A "+ Add" also LIFTS any prior ✕ ('off') so a
  // re-added connector isn't left suppressed by a stale state row. Fans out, then room re-fetches.
  const handleAddAgents = useCallback(async (row, agentIds) => {
    const nid = String(catalogNetworkId || '').trim();
    const pid = String(catalogPoolId || '').trim();
    const ids = (Array.isArray(agentIds) ? agentIds : []).map((a) => String(a || '').trim()).filter(Boolean);
    if (!nid || !row || !ids.length) return;
    const isMcp = row.kind === 'mcp' || row.kind === 'mcp-declared';
    const isDeclaredConnector = row.kind === 'connector-declared'
      || String(row.secretId || '').startsWith('declared');
    try {
      // NETWORK scope (no pool): add to each agent's BASE enabledMcpIds (MCP only for now).
      if (!pid) {
        if (!isMcp) {
          setError('Adding a connector at the network scope isn’t supported yet — do it in the agent’s tools.');
          return;
        }
        for (const aid of ids) {
          await setAgentNetworkMcpEnablement(nid, aid, row.mcpId, true);
        }
        onConnectionsChanged?.();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed', { detail: { source: 'connections-tab' } }));
        }
        refresh(true);
        return;
      }
      for (const aid of ids) {
        if (isMcp) {
          await setAgentMcpState(nid, pid, aid, row.mcpId, 'on');
        } else if (isDeclaredConnector) {
          // No bound secret yet — just enable it for the agent (unconfigured until a secret is set).
          await setAgentConnectorState(nid, pid, aid, row.toolName, 'on');
        } else {
          // Real registry connector: lift any prior ✕ suppression, then bind the row's secret.
          await clearAgentConnectorState(nid, pid, aid, row.toolName);
          await setAgentPoolConnectorOverride(nid, pid, aid, row.toolName, { secretId: row.secretId });
        }
      }
      onConnectionsChanged?.();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed', { detail: { source: 'connections-tab' } }));
      }
      refresh(true);
    } catch (e) {
      setError(e?.message || 'Could not add the connection to those agents');
    }
  }, [catalogNetworkId, catalogPoolId, onConnectionsChanged, refresh]);

  // Round-trip: notify the host that a registry connection was CREATED (only on
  // new entries — edits reuse an existing secretId, nothing new to assign). The
  // wizards' ``onRegistryCreated`` also fires for edits, so the caller passes
  // ``isNew`` to gate. ``saved`` carries ``secretId`` (workspace synthesizes it
  // from the workspaceId; connectors/REST have the real registry id).
  const notifyConnectionCreated = useCallback((toolName, saved, isNew) => {
    if (!isNew || !saved?.secretId) return;
    onConnectionCreated?.({
      tool: toolName,
      secretId: String(saved.secretId).trim(),
      secretName: saved.secretName || toolName,
      scope: saved.scope,
    });
  }, [onConnectionCreated]);

  // Combined "Create New" picker entries: connector tools + authenticated MCPs.
  // MCP entries carry ``__mcpId`` so the pick handler can route them.
  const pickerEntries = useMemo(() => {
    // EVERY platform tool (explicit-only inventory). ACTIVE (selectable) = the registry-wired
    // connectors + internet-search; EVERYTHING ELSE (consult-agent/memorize/recall-memory, s3-explorer,
    // any other catalog/system tool, or a backend comingSoon flag) renders disabled with a "Coming
    // soon" chip. workspace is a synthetic tile added below.
    const connectors = [];
    for (const t of allTools) {
      const name = String(t?.name || '').trim();
      if (!name || name === 'workspace') continue;
      const active = REGISTRY_WIRED_TOOL_NAMES.has(name) || name === 'internet-search';
      const isComingSoon = Boolean(t.comingSoon) || !active;
      connectors.push({
        category: 'connector',
        name,
        displayName: name,
        description: t.description || (isComingSoon ? 'Coming soon' : 'Stepflow tool'),
        comingSoon: isComingSoon,
        __comingSoon: isComingSoon,
        mostUsed: MOST_USED_CONNECTORS.has(name),
      });
    }
    // PLUGINS.md — each installed plugin's connection type is a tile of its own (no catalog function behind it)
    for (const [id, t] of Object.entries(pluginTypes || {})) {
      if (connectors.some((c) => c.name === id)) continue;
      connectors.push({
        category: 'connector', name: id, displayName: t.displayName || t.label || id,
        description: t.wizard?.intro || 'A connection a plugin adds', comingSoon: false, __comingSoon: false,
        mostUsed: false,
      });
    }
    // Synthetic 'workspace' tile — no backing Lambda, injected client-side. The
    // wizard uses the dedicated /tools/workspaces REST surface (upload → unpack).
    connectors.push({
      category: 'connector',
      name: 'workspace',
      displayName: WORKSPACE_PSEUDO_TOOL.displayName,
      description: WORKSPACE_PSEUDO_TOOL.description,
      comingSoon: false,
      mostUsed: true,
    });
    const mcps = mcpRegistry.map((m) => ({
      category: 'mcp',
      name: `mcp:${m.id}`,
      displayName: m.displayName || m.id,
      description: m.comingSoon
        ? 'Setup coming soon'
        : [pluginMcpNote(m, pluginTypes?.[m.connectionType]?.label), m.description || `${m.displayName || m.id} (MCP tool)`]
          .filter(Boolean).join(' — '),
      authRequired: Boolean(m.authRequired),
      verified: Boolean(m.verified),
      __mcpId: m.id,
      __comingSoon: Boolean(m.comingSoon),
      comingSoon: Boolean(m.comingSoon),
      mostUsed: MOST_USED_MCPS.has(m.id),
    }));
    return [...mcps, ...connectors];
  }, [allTools, mcpRegistry, pluginTypes]);

  const openToolPickerForCreate = useCallback(() => {
    setError('');
    if (!allowCreate) return;
    if (!pickerEntries.length) {
      setError('No credential-based tools or MCPs are available for connections.');
      return;
    }
    setWizardInitial(undefined);
    setWizardKind(null);
    setMcpWizard(null);
    setToolPickerOpen(true);
  }, [allowCreate, pickerEntries]);

  const handlePickConnectionTool = useCallback(
    (tool) => {
      setToolPickerOpen(false);
      setError('');
      setWizardInitial(undefined);
      // MCP entry → open the MCP credential wizard at the mount scope.
      if (tool?.__mcpId) {
        const mcp = mcpById[tool.__mcpId];
        if (!mcp) {
          setError(`MCP “${tool.__mcpId}” is not available.`);
          return;
        }
        if (mcp.comingSoon || tool.__comingSoon) {
          setError(`${mcp.displayName || mcp.id} setup is coming soon — not configurable yet.`);
          return;
        }
        // Unauthenticated MCPs have no credential to save — there's nothing to
        // create in the Connections catalog. Tell the user where to enable it
        // instead of opening an empty wizard.
        if (isPluginMcp(mcp)) {               // PLUGINS.md §3 — it runs through the plugin's connection, not a key
          const conn = pluginTypes?.[mcp.connectionType]?.label || mcp.connectionType;
          setError(
            `${mcp.displayName || mcp.id} comes with ${mcp.pluginName || mcp.pluginId} and runs through your ${conn} `
            + `connection — set that up here, then enable ${mcp.displayName || mcp.id} on an agent.`,
          );
          return;
        }
        if (!mcp.authRequired) {
          setError(
            `${mcp.displayName || mcp.id} needs no credential — enable it directly on an agent `
            + `in Tools & Routing or the room drawer; nothing to save here.`,
          );
          return;
        }
        // A DISTINCT per-agent shell (1:1) → configure at AGENT scope for that agent, not the room's
        // shared POOL scope (else 6 distinct repos would collapse onto one shared credential).
        const _aid = String(tool.__agentId || '').trim();
        const _scope = _aid
          ? { kind: 'AGENT', networkId: catalogNetworkId, poolId: catalogPoolId, agentId: _aid }
          : registryScope;
        setMcpWizard({ mcp, scope: _scope, agentId: _aid || undefined });
        return;
      }
      const name = String(tool?.name || '').trim();
      if (name === 'workspace') {
        setWizardKind('workspace');
        return;
      }
      if (name === 'code-connector') {
        if (!codeConnectorTool) {
          setError('code-connector is not available in the catalog.');
          return;
        }
        setWizardKind('codeconnector');
        return;
      }
      if (name === 'generic-rest-api') {
        if (!genericRestTool) {
          setError('generic-rest-api is not available in the catalog.');
          return;
        }
        setWizardKind('rest');
        return;
      }
      if (name === 'cred-broker') {
        setWizardKind('credbroker');
        return;
      }
      if (pluginTypes[name]) {               // PLUGINS.md — a plugin's federated connection (PRODUCT.md §4, R4)
        setFederatedType(name);
        setWizardKind('federated');
        return;
      }
      if (name === 'internet-search') {
        // Active, but native + credential-free — there's nothing to create here. Point to where it's
        // actually enabled (the agent Tool picker), matching the unauth-MCP affordance above.
        setError(
          'internet-search needs no connection — enable it directly on an agent in Tools & Routing; '
          + 'nothing to configure here.',
        );
        return;
      }
      setError(
        `Connection UI for “${name || 'this tool'}” is not implemented — use the Run tab or registry API.`,
      );
    },
    [codeConnectorTool, genericRestTool, mcpById, registryScope, catalogNetworkId, catalogPoolId, pluginTypes],
  );

  // Deep-link consumer: act on ``autoOpenTool`` once (and once the MCP registry
  // is loaded so an MCP id resolves). 'workspace' → workspace wizard; any other
  // value is treated as an MCP id. Calls ``onAutoOpenConsumed`` so re-renders
  // don't re-trigger it.
  useEffect(() => {
    if (!autoOpenTool || !allowCreate) return;
    const t = String(autoOpenTool).trim();
    if (!t) return;
    if (t === 'workspace' || t === 'code-connector' || t === 'generic-rest-api' || t === 'cred-broker' || pluginTypes[t]) {
      handlePickConnectionTool({ name: t });
      onAutoOpenConsumed?.();
      return;
    }
    // Otherwise it's an MCP id — wait until the registry is hydrated so mcpById
    // can resolve it; consume only after we have it (or it's known-absent).
    if (Object.keys(mcpById || {}).length === 0) return;
    handlePickConnectionTool({ __mcpId: t });
    onAutoOpenConsumed?.();
  }, [autoOpenTool, allowCreate, mcpById, handlePickConnectionTool, onAutoOpenConsumed]);

  // MCP wizard save/test — wired to the scoped catalog endpoints. The wizard's
  // ``scope`` (mount scope, or an explicit override) determines the row.
  const handleMcpWizardSave = useCallback(
    async (mcpId, credentials, opts = {}) => {
      const scope = mcpWizard?.scope || registryScope;
      // ``label`` names the instance; the backend mints a fresh instanceId from a
      // non-empty label (and reuses an existing one when editing). Without it
      // every save would collapse onto the single "default" instance.
      const label = String(opts.label || '').trim();
      const existingInstanceId = String(mcpWizard?.instanceId || '').trim();
      // Per-agent (AGENT-scoped) shell → write via the per-agent route (the scoped-create endpoint
      // only handles TENANT/NETWORK/POOL). This credentials THIS agent's own distinct connection.
      const agentId = String(mcpWizard?.agentId || '').trim();
      if (agentId) {
        await putAgentMcpCredential(catalogNetworkId, catalogPoolId, agentId, mcpId, credentials);
        setMcpWizard(null);
        refresh(true);
        onConnectionsChanged?.();
        return;
      }
      const body = { mcpId, scope, credentials };
      if (label) body.label = label;
      if (existingInstanceId) body.instanceId = existingInstanceId;
      const created = await createMcpCredential(body);
      setMcpWizard(null);
      refresh(true);
      // Round-trip: surface the new instance so the host can return + assign it.
      // Only on CREATE (not edit) — an edit reuses an existing instanceId.
      if (!existingInstanceId) {
        onConnectionCreated?.({
          tool: mcpId,
          secretId: String(created?.instanceId || '').trim(),
          secretName: label || (created?.label) || mcpId,
          scope,
        });
      }
    },
    [mcpWizard, registryScope, refresh, onConnectionCreated, onConnectionsChanged,
     catalogNetworkId, catalogPoolId],
  );
  const handleMcpWizardTest = useCallback(
    async (mcpId, credentials) => {
      // The probe is credential-only + ephemeral, so scope is irrelevant to it — but the scoped-test
      // endpoint validates TENANT/NETWORK/POOL. For a per-agent (AGENT) shell, probe at the room's
      // POOL scope so the endpoint accepts it.
      const scope = mcpWizard?.agentId
        ? { kind: 'POOL', networkId: catalogNetworkId, poolId: catalogPoolId }
        : (mcpWizard?.scope || registryScope);
      return testMcpCredentialScoped({ mcpId, scope, credentials });
    },
    [mcpWizard, registryScope, catalogNetworkId, catalogPoolId],
  );
  const handleMcpRowDelete = useCallback(
    async (row) => {
      const busyKey = `${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`;
      setMcpBusyKey(busyKey);
      setError('');
      try {
        // Pass the instanceId so a labeled instance is removed (not the legacy
        // "default") — multiple instances share an mcpId+scope.
        await deleteMcpCredential({
          mcpId: row.mcpId,
          scope: row.scope,
          instanceId: row.instanceId || 'default',
        });
        refresh(true);
      } catch (e) {
        setError(e?.message || 'Could not remove MCP connection');
      } finally {
        setMcpBusyKey('');
      }
    },
    [refresh],
  );

  const copySecretId = useCallback(async (sid) => {
    const text = String(sid || '');
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Cable className="h-4 w-4 shrink-0 text-blue-600" aria-hidden />
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <span className="text-[11px] text-gray-500">
              {filteredRows.length} connection{filteredRows.length === 1 ? '' : 's'}
            </span>
          </div>
          {subtitle ? (
            <p className="pl-6 text-[10px] leading-snug text-gray-400">{subtitle}</p>
          ) : catalogNetworkId && catalogNetworkId !== 'new' ? (
            String(catalogPoolId || '').trim() ? (
              <p className="pl-6 text-[10px] leading-snug text-gray-400">
                Tenant, network, and pool-scoped rows visible to this pool (
                <span className="font-mono">{String(catalogPoolId).trim()}</span>
                ).
              </p>
            ) : (
              <p className="pl-6 text-[10px] leading-snug text-gray-400">
                Tenant-wide connections and connections scoped to this network.
              </p>
            )
          ) : (
            <p className="pl-6 text-[10px] leading-snug text-gray-400">
              Reusable API connections and credentials for MCP tools across agents.
            </p>
          )}
          {!allowCreate && String(catalogPoolId || '').trim()
          && catalogNetworkId
          && catalogNetworkId !== 'new' ? (
            <p className="pl-6 text-[10px] leading-snug text-amber-800">
              View-only: only the pool creator or a workspace admin can create connections here.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openToolPickerForCreate}
            disabled={!allowCreate}
            title={
              allowCreate
                ? 'Add a connection'
                : 'Only pool creators and workspace admins can create connections in this room'
            }
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold text-white transition-colors ${
              allowCreate
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'cursor-not-allowed bg-gray-400'
            }`}
          >
            <Plus className="h-3 w-3" aria-hidden /> Create New
          </button>
          <button
            type="button"
            onClick={() => refresh(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            aria-label="Refresh connections list"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or tool"
            className="rounded-md border border-gray-200 py-1 pl-7 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            data-testid="secrets-catalog-search"
          />
        </div>
        <select
          value={toolFilter}
          onChange={(e) => setToolFilter(e.target.value)}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
          data-testid="secrets-catalog-tool-filter"
        >
          {toolChoices.map((o) => (
            <option key={o.value || '_all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={authFilter}
          onChange={(e) => setAuthFilter(e.target.value)}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
          data-testid="secrets-catalog-auth-filter"
        >
          {authTypeChoices.map((o) => (
            <option key={o.value || '_all_auth'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {probeBanner ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{probeBanner}</div>
      ) : null}

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-1.5 text-left">Name</th>
              <th className="px-3 py-1.5 text-left">Tool</th>
              <th className="px-3 py-1.5 text-left">Auth</th>
              <th className="px-3 py-1.5 text-left">Status</th>
              <th className="px-3 py-1.5 text-left">Scope</th>
              <th className="px-3 py-1.5 text-left">Agents</th>
              <th className="px-3 py-1.5 text-left">Updated</th>
              <th className="px-3 py-1.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  No connections yet — create one or adjust filters.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                // INHERITED connection (framework Phase 4): defined at a broader scope (network /
                // tenant) and reused here — surfaced READ-ONLY so nothing is unnoticed. No actions;
                // edit it at its own scope.
                if (row.kind === 'inherited') {
                  const scopeLabel = row.scope === 'NETWORK' ? 'Network'
                    : row.scope === 'TENANT' ? 'Tenant' : row.scope;
                  return (
                    <tr key={row.rowKey}
                      className="border-t border-gray-100 bg-gray-50/40"
                      data-testid={`secrets-inherited:${row.connKind}:${row.toolId}`}>
                      <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-700">
                        <span className="truncate block" title={row.label}>{row.label}</span>
                        <span className="font-mono text-[10px] text-gray-400">{row.connKind.toUpperCase()}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-700">{row.toolId}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                          title={`Inherited from ${scopeLabel}${row.configured ? '' : ' — not configured there'}`}>
                          Inherited
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600">{scopeLabel}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2 text-right text-[11px] text-gray-400">read-only</td>
                    </tr>
                  );
                }
                // DECLARED MCP (room agents enable it) with no credential row yet — a compact
                // "Configure + Test" row. Configure opens the MCP wizard (same path the picker
                // uses for an MCP id). Mirrors the connector-declared row.
                if (row.kind === 'mcp-declared') {
                  return (
                    <tr key={row.secretId}
                      className={`border-t border-gray-100 ${row.configured ? 'hover:bg-gray-50/80' : 'bg-amber-50/40 hover:bg-amber-50'}`}
                      data-testid={`secrets-declared-mcp:${row.mcpId}${row.scopeId ? `:${row.scopeId}` : ''}`}>
                      <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-900">
                        <span className="truncate block" title={String(row.name || '')}>{String(row.name || row.mcpId)}</span>
                        <span className="font-mono text-[10px] text-gray-400">MCP</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{String(row.toolName || '—')}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2">
                        {row.configured ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            Configured
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                            title="Agents in this room enable this MCP but it has no credential yet. Configure it — runs are blocked until then.">
                            Needs credential
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600">Room</td>
                      <AgentsCell agents={row.agents}
                        onRemove={allowCreate ? (aid) => handleRemoveAgent(row, aid) : null}
                        onAdd={allowCreate ? (aids) => handleAddAgents(row, aids) : null}
                        roomAgents={roomAgents} />
                      <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-500">—</td>
                      <td className="px-3 py-2 text-right">
                        {allowCreate ? (
                          <button
                            type="button"
                            onClick={() => handlePickConnectionTool({
                              __mcpId: row.mcpId,
                              // A 1:1 per-agent shell configures the RIGHT agent's own credential.
                              __agentId: row.agentId || undefined,
                            })}
                            className="rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                            title={row.agentId
                              ? `Configure + test this connection for ${row.agentId}`
                              : "Configure + test this MCP's credential for this room"}
                            data-testid={`secrets-configure-declared-mcp:${row.mcpId}${row.scopeId ? `:${row.scopeId}` : ''}`}
                          >
                            {row.configured ? 'Manage' : 'Configure'}
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-400">read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                }
                // DECLARED connector (room agents need it) with no real row yet — a compact
                // "Configure + Test" row. Clicking Configure opens the same class wizard the
                // "Create New" picker routes to (at the room mount scope).
                if (row.kind === 'connector-declared') {
                  const label = CONNECTOR_CLASS_LABELS[row.connectorClass] || row.connectorClass;
                  return (
                    <tr key={row.secretId}
                      className={`border-t border-gray-100 ${row.configured ? 'hover:bg-gray-50/80' : 'bg-amber-50/40 hover:bg-amber-50'}`}
                      data-testid={`secrets-declared-connector:${row.connectorClass}`}>
                      <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-900">
                        <span className="truncate block" title={label}>{label}</span>
                        <span className="font-mono text-[10px] text-gray-400">CONNECTOR</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{row.connectorClass}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2">
                        {row.configured ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            Configured
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                            title="Agents in this room need this connector but it has no credential yet. Configure it — runs are blocked until then.">
                            Needs credential
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600">Room</td>
                      <AgentsCell agents={row.agents}
                        onRemove={allowCreate ? (aid) => handleRemoveAgent(row, aid) : null}
                        onAdd={allowCreate ? (aids) => handleAddAgents(row, aids) : null}
                        roomAgents={roomAgents} />
                      <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-500">—</td>
                      <td className="px-3 py-2 text-right">
                        {allowCreate ? (
                          <button
                            type="button"
                            onClick={() => handlePickConnectionTool({ name: row.connectorClass })}
                            className="rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                            title="Configure + test this connector's credential for this room"
                            data-testid={`secrets-configure-declared:${row.connectorClass}`}
                          >
                            {row.configured ? 'Manage' : 'Configure'}
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-400">read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                }
                // MCP credential rows have their own (simpler) shape + actions.
                if (row.kind === 'mcp') {
                  const inherited = row._mcpInherited;
                  const busy = mcpBusyKey === `${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`;
                  // READINESS: is this configured MCP's credential server-verified as BAD? Drives the
                  // red "Invalid" badge + reason + a red-tinted row so the fix target is unmistakable.
                  const _ready = (readinessByMcp && row.mcpId) ? readinessByMcp[row.mcpId] : null;
                  const _readyBad = row.configured !== false
                    && String(_ready?.status || '').toUpperCase() === 'BAD';
                  return (
                    <React.Fragment key={row.secretId}>
                    <tr
                      className={`border-t border-gray-100 ${_readyBad ? 'bg-red-50/70 hover:bg-red-50' : 'hover:bg-gray-50/80'}`}>
                      <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-900">
                        <span className="truncate block" title={String(row.secretName || '')}>
                          {String(row.secretName || '—')}
                        </span>
                        <span className="font-mono text-[10px] text-gray-400">MCP</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{String(row.toolName || '—')}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2">
                        {row.configured === false ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                            title="This tool was set up but has no credential yet. Edit it to add one — runs are blocked until then.">
                            Needs credential
                          </span>
                        ) : _readyBad ? (
                          <span
                            className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-800"
                            data-testid={`secrets-readiness-bad:${row.mcpId}`}
                            title={_ready?.reason || 'Credential failed verification — edit + re-test.'}
                          >
                            Expired or invalid
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            Configured
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600">
                        {formatScope(row)}
                        {isSharedFromLead(row) ? (
                          <span
                            className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                            title={`Shared from the Lead room (${leadPoolId}) — this linked room uses its Lead's connection.`}
                          >
                            Shared
                          </span>
                        ) : inherited ? (
                          <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                            Inherited
                          </span>
                        ) : null}
                      </td>
                      <AgentsCell agents={mcpAgentsByKey[`${row.mcpId}:${String(row.scope?.kind || '').toUpperCase()}`]}
                        onRemove={allowCreate ? (aid) => handleRemoveAgent(row, aid) : null}
                        onAdd={allowCreate ? (aids) => handleAddAgents(row, aids) : null}
                        roomAgents={roomAgents} />
                      <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-500">
                        {String(row.updatedAt || '—')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-wrap items-center justify-end gap-1">
                          {!allowCreate ? (
                            <span className="text-[11px] text-gray-400">read-only</span>
                          ) : inherited ? (
                            // Inherited (wider-scope, e.g. NETWORK) rows are
                            // DISPLAY-ONLY here — they can only be edited at the
                            // level that owns them (the network's Connections).
                            // No room-level override.
                            <span
                              className="text-[11px] text-gray-400"
                              title={`Inherited from ${formatScope(row)} — edit it at that level`}
                            >
                              Inherited
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                disabled={mcpBusyKey === `edit:${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`}
                                onClick={async () => {
                                  const mcp = mcpById[row.mcpId];
                                  if (!mcp) { setError(`MCP “${row.mcpId}” is not available.`); return; }
                                  // Editing → carry the existing instance so the
                                  // save updates THIS instance (not a new one), and
                                  // rehydrate saved NON-SECRET values so the wizard
                                  // opens pre-populated (parity with connectors).
                                  const ek = `edit:${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`;
                                  setMcpBusyKey(ek);
                                  setError('');
                                  let revealed = null;
                                  try {
                                    revealed = await revealMcpCredentialScoped({
                                      mcpId: row.mcpId,
                                      scope: row.scope,
                                      instanceId: row.instanceId || 'default',
                                    });
                                  } catch (e) {
                                    // Soft-fail: still open the wizard (blank prefill)
                                    // rather than block editing entirely.
                                    console.warn('[SecretsCatalog] MCP reveal failed:', e);
                                  } finally {
                                    setMcpBusyKey('');
                                  }
                                  setMcpWizard({
                                    mcp,
                                    scope: row.scope,
                                    instanceId: row.instanceId,
                                    label: revealed?.label || row.label,
                                    initialValues: revealed?.values || null,
                                    initialSecretsSet: revealed?.secretsSet || null,
                                  });
                                }}
                                className="rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                title="Edit / replace this connection's credential"
                                data-testid={`secrets-edit-mcp:${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind || 'TENANT'}`}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleMcpRowDelete(row)}
                                className="rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                {busy ? 'Removing…' : 'Remove'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Second deck: the last probe error, full-width, so the fixer sees exactly
                        why the fail-fast gate is blocking the trigger. */}
                    {_readyBad ? <ProbeErrorDeckRow message={_ready?.reason} /> : null}
                    </React.Fragment>
                  );
                }
                const canMutateRow = canEditDeleteRegistrySecret({
                  row,
                  catalogNetworkId,
                  catalogPoolId,
                  workspaceRole,
                  poolRole,
                  viewerUserSub,
                  networkOwnerUserId,
                  allowTenantRegistryMutate,
                });
                const mutateLockedHint = registryRowMutateHint(row, { allowTenantRegistryMutate });
                const inheritedHere = registrySecretInheritedHere(row, catalogNetworkId, catalogPoolId);
                // A configured connector whose last probe/test FAILED — highlight the row + carry
                // the captured error to the second deck (same treatment as a BAD MCP credential).
                const _testFailed = String(row.testStatus || '').toUpperCase() === 'TESTED_FAIL';
                return (
                <React.Fragment key={String(row.secretId)}>
                <tr className={`border-t border-gray-100 ${_testFailed ? 'bg-red-50/70 hover:bg-red-50' : 'hover:bg-gray-50/80'}`}>
                  <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-900">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate" title={String(row.secretName || '')}>
                        {String(row.secretName || '—')}
                      </span>
                      {isSharedFromLead(row) ? (
                        // §13: this connection/workspace belongs to the LEAD room and is SHARED with
                        // this linked room (peer of the network "Inherited" badge). Read-only here —
                        // manage it in the Lead room.
                        <span
                          className="shrink-0 rounded bg-indigo-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-indigo-700"
                          title={`Shared from the Lead room (${row.sharedFromLeadPool || leadPoolId}) — this linked room uses its Lead's connection. Manage it in the Lead room.`}
                          data-testid={`secrets-shared-${row.secretId || ''}`}
                        >
                          Shared
                        </span>
                      ) : inheritedHere ? (
                        <span
                          className="shrink-0 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-gray-500"
                          title="Inherited from a higher scope (tenant / network). Shown here because it applies to this context — manage it at the scope it was created."
                          data-testid={`secrets-inherited-${row.secretId || ''}`}
                        >
                          Inherited
                        </span>
                      ) : null}
                    </span>
                    <span className="font-mono text-[10px] text-gray-400">{String(row.secretId || '').slice(0, 12)}…</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{String(row.toolName || '—')}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-700">{formatAuthType(row)}</td>
                  <td className="px-3 py-2">
                    {row.configured === false ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                        title="This connection was set up but has no credential yet. Edit it to add one — runs are blocked until then.">
                        Needs credential
                      </span>
                    ) : (
                      <RegistryTestStatusBadge row={row} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600">{formatScope(row)}</td>
                  <AgentsCell agents={connectorAgentsBySecretId[row.secretId]}
                    onRemove={allowCreate ? (aid) => handleRemoveAgent(row, aid) : null}
                    onAdd={(allowCreate && CONNECTOR_CLASS_LABELS[row.toolName] && row.secretId)
                      ? (aids) => handleAddAgents(row, aids) : null}
                    roomAgents={roomAgents} />
                  <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-500">
                    {String(row.updatedAt || row.updated_at || row.createdAt || '—')}
                    {row.lastTestedAt ? (
                      <span className="mt-0.5 block text-[10px] text-gray-400" title={row.lastTestError || ''}>
                        Tested {String(row.lastTestedAt)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {String(row.toolName || '').trim() === 'workspace' ? (
                        <>
                          <button
                            type="button"
                            title="View this workspace's files + which template / skill versions were baked in (read-only)"
                            onClick={() => setViewingWorkspaceId(String(row.secretId || '').trim())}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
                            data-testid={`workspace-view-${row.secretId}`}
                          >
                            <Eye className="h-3 w-3 shrink-0" aria-hidden /> View
                          </button>
                          {/* Edit = add/remove SKILLS (source files are immutable).
                              Gated by the same down-only authorship as connectors,
                              so it never shows on an inherited (wider-scope) row. */}
                          <button
                            type="button"
                            disabled={!canMutateRow}
                            title={canMutateRow ? 'Add or remove skills for this workspace' : mutateLockedHint}
                            onClick={() => {
                              setError('');
                              setWizardInitial({
                                secretId: String(row.secretId || '').trim(),
                                secretName: row.secretName,
                                scope: row.scope,
                                provenance: row.provenance || (row.config && row.config.provenance),
                              });
                              setWizardKind('workspace');
                            }}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`workspace-edit-${row.secretId}`}
                          >
                            <Pencil className="h-3 w-3 shrink-0" aria-hidden /> Edit
                          </button>
                        </>
                      ) : null}
                      {connectionToolNameSet.has(String(row.toolName || '').trim()) ? (
                        <>
                          <button
                            type="button"
                            disabled={
                              !canMutateRow ||
                              editLoadingSecretId === String(row.secretId || '') ||
                              deletingSecretId === String(row.secretId || '') ||
                              Boolean(testingSecretId && testingSecretId === String(row.secretId || ''))
                            }
                            title={
                              canMutateRow
                                ? 'Edit connection, re-test, and save'
                                : mutateLockedHint
                            }
                            onClick={async () => {
                              const sid = String(row.secretId || '').trim();
                              if (!sid) return;
                              setError('');
                              setProbeBanner('');
                              setEditLoadingSecretId(sid);
                              try {
                                const { secret } = await getSecretsRegistrySecret(sid);
                                const tn = String(secret.toolName || row.toolName || '').trim();
                                if (tn === 'code-connector') {
                                  setWizardInitial(mapRegistryCodeConnectorSecretToInitial(secret));
                                  setWizardKind('codeconnector');
                                } else if (tn === 'cred-broker') {
                                  setWizardInitial(mapRegistryCredBrokerSecretToInitial(secret));
                                  setWizardKind('credbroker');
                                } else if (pluginTypes[tn]) {
                                  setWizardInitial(mapRegistryFederatedToInitial(secret, pluginTypes[tn]));
                                  setFederatedType(tn);
                                  setWizardKind('federated');
                                } else {
                                  setWizardInitial(mapRegistrySecretToWizardInitial(secret));
                                  setWizardKind('rest');
                                }
                              } catch (e) {
                                setError(e?.message || 'Could not load connection for editing');
                              } finally {
                                setEditLoadingSecretId('');
                              }
                            }}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`secrets-edit-${row.secretId}`}
                          >
                            {editLoadingSecretId === String(row.secretId || '') ? (
                              'Loading…'
                            ) : (
                              <>
                                <Pencil className="h-3 w-3 shrink-0" aria-hidden /> Edit
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            disabled={
                              testingSecretId === String(row.secretId || '') ||
                              editLoadingSecretId === String(row.secretId || '') ||
                              deletingSecretId === String(row.secretId || '')
                            }
                            title={
                              String(row.toolName || '') === 'generic-rest-api'
                                ? 'Run a read-only HTTP probe using stored credentials (authorized by the tools API)'
                                : 'Run the catalog validation test for this connection'
                            }
                            onClick={async () => {
                              const sid = String(row.secretId || '').trim();
                              if (!sid) return;
                              const tn = String(row.toolName || '').trim();
                              const label = String(row.secretName || sid || 'Connection');
                              setProbeBanner('');
                              setTestingSecretId(sid);
                              try {
                                const opts =
                                  tn === 'generic-rest-api'
                                    ? {
                                        networkId: REGISTRY_PROBE_NETWORK_ID,
                                        probeRequest: probeRequestFromGenericRestRegistryRow(row),
                                      }
                                    : { networkId: REGISTRY_PROBE_NETWORK_ID };
                                const data = await testSecretsRegistrySecret(sid, opts);
                                setRegistryProbeDialog({ title: `Connection test · ${label}`, payload: data });
                                await refresh(true);
                              } catch (e) {
                                setProbeBanner(e?.message || 'Probe request failed');
                              } finally {
                                setTestingSecretId('');
                              }
                            }}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`secrets-test-${row.secretId}`}
                          >
                            {testingSecretId === String(row.secretId || '') ? 'Testing…' : 'Test'}
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        disabled={
                          !canMutateRow ||
                          deletingSecretId === String(row.secretId || '') ||
                          Boolean(testingSecretId && testingSecretId === String(row.secretId || '')) ||
                          editLoadingSecretId === String(row.secretId || '')
                        }
                        title={
                          canMutateRow
                            ? 'Remove this connection (blocked while any agent still references it)'
                            : mutateLockedHint
                        }
                        onClick={() => {
                          const sid = String(row.secretId || '').trim();
                          const label = String(row.secretName || sid || 'connection');
                          if (!sid) return;
                          setProbeBanner('');
                          // Type-to-confirm dialog replaces window.confirm — match
                          // the house DeleteNetworkDialog pattern.
                          setPendingDelete({
                            secretId: sid,
                            name: label,
                            isWorkspace: String(row.toolName || '').trim() === 'workspace',
                          });
                        }}
                        className="inline-flex items-center gap-0.5 rounded border border-red-200 px-1.5 py-0.5 text-[10px] font-medium text-red-800 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid={`secrets-delete-${row.secretId}`}
                      >
                        {deletingSecretId === String(row.secretId || '') ? (
                          'Deleting…'
                        ) : (
                          <>
                            <Trash2 className="h-3 w-3 shrink-0" aria-hidden /> Delete
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        title="Copy connection id"
                        onClick={() => copySecretId(row.secretId)}
                        className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
                        data-testid={`secrets-copy-id-${row.secretId}`}
                      >
                        <Copy className="h-3 w-3" aria-hidden />
                        Copy ID
                      </button>
                      <WhereUsedMenu secretId={String(row.secretId || '')} />
                    </div>
                  </td>
                </tr>
                {/* Second deck: the last connection-test error, full-width. */}
                {_testFailed ? <ProbeErrorDeckRow message={row.lastTestError} /> : null}
                </React.Fragment>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      <ToolSelectDialog
        open={toolPickerOpen}
        entries={pickerEntries}
        onPick={(t) => handlePickConnectionTool(t)}
        onCancel={() => setToolPickerOpen(false)}
        title="Add a connection"
        subtitle="Pick a tool to connect. MCP tools are the modern, recommended path."
        testidPrefix="secrets-catalog"
        dialogTestId="secrets-catalog-tool-picker"
        cardTestId={(t) => `secrets-catalog-pick-tool-${t.name}`}
      />

      {mcpWizard ? (
        <McpCredentialWizardDialog
          mcp={mcpWizard.mcp}
          scope={mcpWizard.scope}
          initialLabel={mcpWizard.label || ''}
          initialValues={mcpWizard.initialValues || null}
          initialSecretsSet={mcpWizard.initialSecretsSet || null}
          onSave={handleMcpWizardSave}
          onTest={handleMcpWizardTest}
          onCancel={() => setMcpWizard(null)}
        />
      ) : null}

      {wizardKind === 'rest' && genericRestTool && tenantId ? (
        <ToolGenericRestConfigWizard
          key={wizardInitial?.secretId || 'registry-new-rest'}
          tool={genericRestTool}
          open
          isNewEntry={!wizardInitial?.secretId}
          persistTarget="registry"
          registryScope={registryScope}
          initialEntry={wizardInitial}
          networkId={REGISTRY_PROBE_NETWORK_ID}
          agentId="registry-ui"
          env={TOOLS_ENV}
          tenantId={tenantId}
          onRegistryCreated={(saved) => {
            const isNew = !wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            notifyConnectionCreated('generic-rest-api', saved, isNew);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      {wizardKind === 'codeconnector' && tenantId ? (
        <ToolCodeConnectorRegistryWizard
          key={wizardInitial?.secretId || 'registry-new-cc'}
          tool={codeConnectorTool || { name: 'code-connector' }}
          open
          isNewEntry={!wizardInitial?.secretId}
          initialEntry={wizardInitial}
          registryScope={registryScope}
          tenantId={tenantId}
          networkId={REGISTRY_PROBE_NETWORK_ID}
          onRegistryCreated={(saved) => {
            const isNew = !wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            notifyConnectionCreated('code-connector', saved, isNew);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      {wizardKind === 'credbroker' && tenantId ? (
        <ToolCredBrokerConfigWizard
          key={wizardInitial?.secretId || 'registry-new-credbroker'}
          tool={{ name: 'cred-broker' }}
          open
          isNewEntry={!wizardInitial?.secretId}
          initialEntry={wizardInitial}
          registryScope={registryScope}
          tenantId={tenantId}
          persistTarget="registry"
          onRegistryCreated={(saved) => {
            const isNew = !wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            notifyConnectionCreated('cred-broker', saved, isNew);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      {wizardKind === 'federated' && tenantId && pluginTypes[federatedType] ? (
        <PluginConnectionWizard
          key={wizardInitial?.secretId || `registry-new-${federatedType}`}
          open
          spec={pluginTypes[federatedType]}
          initialEntry={wizardInitial}
          registryScope={registryScope}
          onRegistryCreated={(saved) => {
            const isNew = !wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed', { detail: { secretId: saved?.secretId } }));
              }
            } catch { /* ignore */ }
            refresh(true);
            notifyConnectionCreated(federatedType, saved, isNew);
          }}
          onCancel={() => { closeWizards(); }}
        />
      ) : null}
      {wizardKind === 'workspace' && tenantId ? (
        <ToolWorkspaceRegistryWizard
          key={wizardInitial?.secretId ? `registry-edit-workspace-${wizardInitial.secretId}` : 'registry-new-workspace'}
          open
          isNewEntry={!wizardInitial?.secretId}
          initialEntry={wizardInitial}
          registryScope={registryScope}
          tenantId={tenantId}
          onRegistryCreated={(saved) => {
            const wasEdit = !!wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            // Only announce a NEW connection on create; an edit just refreshes.
            if (!wasEdit) notifyConnectionCreated('workspace', saved, true);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      <RegistryProbeResultDialog
        open={Boolean(registryProbeDialog)}
        title={registryProbeDialog?.title || 'Connection test'}
        payload={registryProbeDialog?.payload}
        onClose={() => setRegistryProbeDialog(null)}
      />
      <DeleteConnectionDialog
        open={Boolean(pendingDelete)}
        connectionName={pendingDelete?.name || ''}
        isWorkspace={Boolean(pendingDelete?.isWorkspace)}
        onClose={() => {
          if (deletingSecretId) return; // don't dismiss mid-delete
          setPendingDelete(null);
        }}
        onConfirm={async () => {
          const sid = String(pendingDelete?.secretId || '').trim();
          if (!sid) return;
          setDeletingSecretId(sid);
          try {
            // Workspaces delete via their own REST surface (also removes the S3
            // objects); connectors via the secrets-registry. Both reject (409)
            // while an agent still pins the id.
            if (pendingDelete?.isWorkspace) {
              await deleteWorkspace(sid);
            } else {
              await deleteSecretsRegistrySecret(sid);
            }
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('stepflow-secret-registry-changed', { detail: {} }),
              );
            }
            await refresh(true);
            setPendingDelete(null);
          } catch (e) {
            const base = e?.message || 'Delete failed';
            const extra =
              e?.status === 409 &&
              typeof e?.data?.count === 'number' &&
              Number.isFinite(e.data.count)
                ? ` (${e.data.count} agent mount(s); remove mounts first)`
                : '';
            // Surface in the row banner too, then re-throw so the dialog shows it.
            setProbeBanner(`${base}${extra}`);
            throw new Error(`${base}${extra}`);
          } finally {
            setDeletingSecretId('');
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(pendingAgentRemoval)}
        title="Remove agent from this connection?"
        message={pendingAgentRemoval
          ? `“${pendingAgentRemoval.agentId}” will stop using “${pendingAgentRemoval.what}” in this room. This is reversible — re-add the agent anytime.`
          : ''}
        confirmLabel="Remove"
        danger
        busy={removingAgent}
        onConfirm={confirmRemoveAgent}
        onCancel={() => { if (!removingAgent) setPendingAgentRemoval(null); }}
        testid="secrets-remove-agent-confirm"
      />
      {viewingWorkspaceId ? (() => {
        const wrow = rows.find((r) => String(r.secretId || '') === viewingWorkspaceId) || {};
        return (
          <WorkspaceViewDialog
            workspaceId={viewingWorkspaceId}
            name={wrow.secretName}
            provenance={wrow.provenance || (wrow.config && wrow.config.provenance)}
            onClose={() => setViewingWorkspaceId('')}
          />
        );
      })() : null}
    </div>
  );
}
